import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { 
    ChevronLeft, ChevronRight, ExternalLink, Star, DollarSign, Users, TrendingUp,
    BookOpen, Megaphone, Mail, Share2, Zap, Crown, Check, Clock, Target,
    Rocket, Award, Calendar, BarChart3, Globe, Shield, Sparkles, Send
} from 'lucide-react';

// Ultimate Book Launch System - Comprehensive Model
const UltimateBookLaunch = ({ onBack }) => {
    const [activePhase, setActivePhase] = useState(0);
    const [selectedPackage, setSelectedPackage] = useState('bestseller');
    const [checklist, setChecklist] = useState({});

    // ========================
    // COMPREHENSIVE SERVICE DATABASE
    // ========================

    // Pre-Launch Reviewer Networks (Total Reach: 1M+ reviewers)
    const prelaunchServices = {
        arc_distribution: [
            {
                id: 'netgalley',
                name: 'NetGalley',
                reach: '600,000+ reviewers',
                cost: '$450-$500/title (or $45-100 via co-ops)',
                reviewRate: '15-25%',
                leadTime: '6-8 weeks',
                type: 'premium',
                audience: 'Librarians, bloggers, professional reviewers',
                features: ['Industry standard', 'Library acquisition influence', 'Trade review visibility'],
                autoSubmit: true,
                apiAvailable: false,
                url: 'https://www.netgalley.com/pub_register'
            },
            {
                id: 'booksirens',
                name: 'BookSirens',
                reach: '30,000+ vetted reviewers',
                cost: '$10/title + $2/download',
                reviewRate: '75%',
                leadTime: '2-4 weeks',
                type: 'standard',
                audience: 'Genre-focused readers, Amazon reviewers',
                features: ['Pay-per-download', 'Genre targeting', 'High review rate'],
                autoSubmit: true,
                apiAvailable: true,
                url: 'https://booksirens.com/authors'
            },
            {
                id: 'booksprout',
                name: 'BookSprout',
                reach: '20,000+ readers',
                cost: 'Free - $29/month',
                reviewRate: '60-70%',
                leadTime: '2-3 weeks',
                type: 'budget',
                audience: 'Indie readers, audiobook fans',
                features: ['Audiobook support', 'Automated reminders', 'Free tier available'],
                autoSubmit: true,
                apiAvailable: true,
                url: 'https://booksprout.co'
            },
            {
                id: 'hiddengems',
                name: 'Hidden Gems',
                reach: '15,000+ readers',
                cost: '$20-30/campaign',
                reviewRate: '80%',
                leadTime: '1-2 weeks',
                type: 'standard',
                audience: 'Fast-acting reviewers',
                features: ['Highest review rate', 'Email list building', 'Quick turnaround'],
                autoSubmit: true,
                apiAvailable: false,
                url: 'https://www.hiddengemsbooks.com'
            },
            {
                id: 'librarything',
                name: 'LibraryThing Early Reviewers',
                reach: '2M+ members',
                cost: 'FREE',
                reviewRate: '40-50%',
                leadTime: '4-6 weeks',
                type: 'free',
                audience: 'Avid readers, catalogers',
                features: ['Completely free', 'Algorithm-matched readers', 'Cross-posting encouraged'],
                autoSubmit: true,
                apiAvailable: false,
                url: 'https://www.librarything.com/ner/rulesofferer'
            },
            {
                id: 'reedsy',
                name: 'Reedsy Discovery',
                reach: 'Curated reviewers',
                cost: '$50/review',
                reviewRate: '90%+ (guaranteed)',
                leadTime: '2-4 weeks',
                type: 'premium',
                audience: 'Professional reviewers',
                features: ['Guaranteed reviews', 'Professional quality', 'Marketing integration'],
                autoSubmit: true,
                apiAvailable: true,
                url: 'https://reedsy.com/discovery'
            },
            {
                id: 'edelweiss',
                name: 'Edelweiss+',
                reach: '230,000+ industry professionals',
                cost: '$200/year + $90/title',
                reviewRate: '6% (selective)',
                leadTime: '8-12 weeks',
                type: 'premium',
                audience: 'Booksellers, librarians, buyers',
                features: ['B2B catalog', 'Trade visibility', 'Library orders'],
                autoSubmit: true,
                apiAvailable: false,
                url: 'https://www.edelweiss.plus'
            },
            {
                id: 'voraciousreaders',
                name: 'Voracious Readers Only',
                reach: '10,000+ readers',
                cost: '$15-25/campaign',
                reviewRate: '65%',
                leadTime: '2-3 weeks',
                type: 'budget',
                audience: 'Genre readers',
                features: ['Budget-friendly', 'Good review rate', 'Quick setup'],
                autoSubmit: true,
                apiAvailable: false,
                url: 'https://voraciousreadersonly.com'
            }
        ],
        reader_magnets: [
            {
                id: 'bookfunnel',
                name: 'BookFunnel',
                reach: '100,000+ cross-promo readers',
                cost: '$20-150/year',
                features: ['Newsletter swaps', 'Group promos', 'Delivery automation'],
                listBuildRate: '30-50 subscribers/swap',
                autoSubmit: true,
                apiAvailable: true,
                url: 'https://bookfunnel.com'
            },
            {
                id: 'storyorigin',
                name: 'StoryOrigin',
                reach: '50,000+ cross-promo readers',
                cost: '$60-100/year',
                features: ['Newsletter swaps', 'Review copies', 'Analytics'],
                listBuildRate: '20-40 subscribers/swap',
                autoSubmit: true,
                apiAvailable: true,
                url: 'https://storyoriginapp.com'
            },
            {
                id: 'prolificworks',
                name: 'Prolific Works',
                reach: '30,000+ readers',
                cost: '$20-50/month',
                features: ['Giveaways', 'Reader tracking', 'Multi-format delivery'],
                listBuildRate: '15-30 subscribers/giveaway',
                autoSubmit: true,
                apiAvailable: false,
                url: 'https://www.prolificworks.com'
            }
        ],
        giveaway_platforms: [
            {
                id: 'goodreads',
                name: 'Goodreads Giveaways',
                reach: '90M+ members',
                cost: '$119-599/giveaway',
                entries: '500-5,000 typical',
                features: ['Massive reach', 'To-read list additions', 'Social sharing'],
                autoSubmit: true,
                apiAvailable: false,
                url: 'https://www.goodreads.com/giveaway/new'
            },
            {
                id: 'storygraph',
                name: 'StoryGraph Giveaways',
                reach: '1M+ members',
                cost: 'Free-$50',
                entries: '200-1,000 typical',
                features: ['Growing platform', 'Engaged readers', 'Algorithm recommendations'],
                autoSubmit: true,
                apiAvailable: false,
                url: 'https://www.thestorygraph.com'
            }
        ]
    };

    // Launch Day Promotion Services (Total Reach: 2M+ readers)
    const launchServices = [
        {
            id: 'bookbub_featured',
            name: 'BookBub Featured Deals',
            reach: '10,000,000+',
            cost: '$152-$1,000+',
            effectiveness: 5,
            acceptanceRate: '20-25%',
            category: 'premium',
            bestFor: 'Maximum visibility',
            avgDownloads: '1,000-20,000',
            autoSubmit: true
        },
        {
            id: 'freebooksy',
            name: 'Freebooksy',
            reach: '230,000+',
            cost: '$40-$100',
            effectiveness: 5,
            acceptanceRate: '80%+',
            category: 'standard',
            bestFor: 'Free book promos',
            avgDownloads: '2,000-40,000',
            autoSubmit: true
        },
        {
            id: 'ent',
            name: 'eReader News Today',
            reach: '150,000+',
            cost: '$45-$75',
            effectiveness: 4,
            acceptanceRate: '75%+',
            category: 'standard',
            bestFor: 'Discounted books',
            avgDownloads: '500-2,000',
            autoSubmit: true
        },
        {
            id: 'fussylibrarian',
            name: 'Fussy Librarian',
            reach: '200,000+',
            cost: '$12-$25',
            effectiveness: 4,
            acceptanceRate: '85%+',
            category: 'budget',
            bestFor: 'Higher-priced books',
            avgDownloads: '300-1,500',
            autoSubmit: true
        },
        {
            id: 'bargainbooksy',
            name: 'Bargain Booksy',
            reach: '170,000+',
            cost: '$20-$240',
            effectiveness: 4,
            acceptanceRate: '80%+',
            category: 'standard',
            bestFor: 'Nonfiction, bargains',
            avgDownloads: '200-1,000',
            autoSubmit: true
        },
        {
            id: 'robinreads',
            name: 'Robin Reads',
            reach: '190,000+',
            cost: '$20-$40',
            effectiveness: 3,
            acceptanceRate: '90%+',
            category: 'budget',
            bestFor: 'Budget campaigns',
            avgDownloads: '500-1,500',
            autoSubmit: true
        },
        {
            id: 'bookgorilla',
            name: 'BookGorilla',
            reach: '500,000+',
            cost: '$50-$150',
            effectiveness: 3,
            acceptanceRate: '70%+',
            category: 'standard',
            bestFor: 'Daily deals',
            avgDownloads: '100-500',
            autoSubmit: true
        },
        {
            id: 'bookbarbarian',
            name: 'Book Barbarian',
            reach: '80,000+',
            cost: '$20-$40',
            effectiveness: 3,
            acceptanceRate: '85%+',
            category: 'budget',
            bestFor: 'SFF genres',
            avgDownloads: '200-800',
            autoSubmit: true
        },
        {
            id: 'bknights',
            name: 'BKnights (Fiverr)',
            reach: 'Variable',
            cost: '$5-$50',
            effectiveness: 3,
            acceptanceRate: '95%+',
            category: 'budget',
            bestFor: 'Testing, supplementing',
            avgDownloads: '50-300',
            autoSubmit: false
        },
        {
            id: 'kindlenationdaily',
            name: 'Kindle Nation Daily',
            reach: '100,000+',
            cost: '$30-$150',
            effectiveness: 3,
            acceptanceRate: '80%+',
            category: 'standard',
            bestFor: 'Kindle readers',
            avgDownloads: '100-400',
            autoSubmit: true
        },
        {
            id: 'ohfb',
            name: 'OHFB',
            reach: '120,000+',
            cost: '$30-$60',
            effectiveness: 3,
            acceptanceRate: '75%+',
            category: 'standard',
            bestFor: 'Romance, mystery',
            avgDownloads: '150-600',
            autoSubmit: true
        },
        {
            id: 'readingdeals',
            name: 'Reading Deals',
            reach: '80,000+',
            cost: '$15-$35',
            effectiveness: 3,
            acceptanceRate: '85%+',
            category: 'budget',
            bestFor: 'Multi-genre',
            avgDownloads: '100-400',
            autoSubmit: true
        }
    ];

    // Advertising Platforms
    const advertisingPlatforms = [
        {
            id: 'bookbub_ads',
            name: 'BookBub Ads',
            type: 'Self-serve',
            minBudget: '$5/day',
            targeting: 'Author targeting, genre, platform',
            bestCTR: '14%+',
            features: ['Target competitor readers', 'Flexible budgets', 'Real-time bidding']
        },
        {
            id: 'amazon_ads',
            name: 'Amazon Ads',
            type: 'Self-serve',
            minBudget: '$1/day',
            targeting: 'Keywords, products, categories',
            bestCTR: '0.3-0.5%',
            features: ['Direct to purchase', 'Keyword discovery', 'Automatic campaigns']
        },
        {
            id: 'facebook_ads',
            name: 'Facebook/Meta Ads',
            type: 'Self-serve',
            minBudget: '$5/day',
            targeting: 'Interests, behaviors, lookalikes',
            bestCTR: '1-2%',
            features: ['Wide reach', 'Video ads', 'Retargeting']
        }
    ];

    // ========================
    // LAUNCH PHASES
    // ========================

    const launchPhases = [
        {
            name: 'Foundation',
            timeframe: '12-8 weeks before',
            icon: <Target className="h-5 w-5" />,
            color: 'text-blue-500',
            tasks: [
                { id: 'metadata', name: 'Optimize book metadata (keywords, categories)', service: null },
                { id: 'cover', name: 'Finalize professional cover design', service: null },
                { id: 'description', name: 'Write compelling book description', service: null },
                { id: 'reader_magnet', name: 'Create reader magnet (free prequel/bonus)', service: 'bookfunnel' },
                { id: 'email_setup', name: 'Set up email marketing automation', service: null },
                { id: 'netgalley_submit', name: 'Submit to NetGalley', service: 'netgalley', reach: '600K reviewers' },
                { id: 'edelweiss_submit', name: 'Submit to Edelweiss+', service: 'edelweiss', reach: '230K professionals' }
            ],
            totalReach: '830,000+'
        },
        {
            name: 'ARC Campaign',
            timeframe: '8-4 weeks before',
            icon: <Users className="h-5 w-5" />,
            color: 'text-purple-500',
            tasks: [
                { id: 'booksirens', name: 'Launch BookSirens campaign', service: 'booksirens', reach: '30K reviewers' },
                { id: 'booksprout', name: 'Distribute via BookSprout', service: 'booksprout', reach: '20K readers' },
                { id: 'hiddengems', name: 'Submit to Hidden Gems', service: 'hiddengems', reach: '15K readers' },
                { id: 'librarything', name: 'Apply to LibraryThing Early Reviewers', service: 'librarything', reach: '2M members' },
                { id: 'reedsy', name: 'Request Reedsy Discovery reviews', service: 'reedsy', reach: 'Curated' },
                { id: 'voracious', name: 'Submit to Voracious Readers', service: 'voraciousreaders', reach: '10K readers' },
                { id: 'goodreads_giveaway', name: 'Run Goodreads Giveaway', service: 'goodreads', reach: '90M members' }
            ],
            totalReach: '92,075,000+'
        },
        {
            name: 'List Building',
            timeframe: '4-2 weeks before',
            icon: <Mail className="h-5 w-5" />,
            color: 'text-green-500',
            tasks: [
                { id: 'bookfunnel_swap', name: 'Join BookFunnel newsletter swaps (5-10)', service: 'bookfunnel', reach: '10K-50K readers' },
                { id: 'storyorigin_swap', name: 'Join StoryOrigin promos (3-5)', service: 'storyorigin', reach: '5K-20K readers' },
                { id: 'group_promo', name: 'Participate in genre group promotion', service: 'bookfunnel', reach: '20K-100K readers' },
                { id: 'preorder_push', name: 'Push preorders via email list', service: null },
                { id: 'cover_reveal', name: 'Coordinate cover reveal with bloggers', service: null },
                { id: 'social_teasers', name: 'Daily social media teasers', service: null }
            ],
            totalReach: '35,000-170,000'
        },
        {
            name: 'Launch Week',
            timeframe: 'Week of launch',
            icon: <Rocket className="h-5 w-5" />,
            color: 'text-red-500',
            tasks: [
                { id: 'bookbub_featured', name: 'BookBub Featured Deal (if accepted)', service: 'bookbub_featured', reach: '10M readers' },
                { id: 'freebooksy', name: 'Freebooksy promotion', service: 'freebooksy', reach: '230K readers' },
                { id: 'ent', name: 'eReader News Today', service: 'ent', reach: '150K readers' },
                { id: 'fussy', name: 'Fussy Librarian', service: 'fussylibrarian', reach: '200K readers' },
                { id: 'robin', name: 'Robin Reads', service: 'robinreads', reach: '190K readers' },
                { id: 'bargain', name: 'Bargain Booksy', service: 'bargainbooksy', reach: '170K readers' },
                { id: 'gorilla', name: 'BookGorilla', service: 'bookgorilla', reach: '500K readers' },
                { id: 'email_blast', name: 'Full email list announcement', service: null },
                { id: 'amazon_ads', name: 'Launch Amazon Ads campaigns', service: 'amazon_ads' },
                { id: 'bookbub_ads', name: 'Launch BookBub Ads', service: 'bookbub_ads' }
            ],
            totalReach: '11,440,000+'
        },
        {
            name: 'Momentum',
            timeframe: 'Weeks 2-4',
            icon: <TrendingUp className="h-5 w-5" />,
            color: 'text-orange-500',
            tasks: [
                { id: 'review_followup', name: 'Follow up with ARC readers for reviews', service: null },
                { id: 'secondary_promos', name: 'Second wave of promo sites', service: null, reach: '500K+ readers' },
                { id: 'adjust_ads', name: 'Optimize performing ad campaigns', service: null },
                { id: 'blogger_outreach', name: 'Blogger interview circuit', service: null },
                { id: 'podcast_guest', name: 'Podcast guest appearances', service: null },
                { id: 'storygraph', name: 'StoryGraph giveaway', service: 'storygraph', reach: '1M members' }
            ],
            totalReach: '1,500,000+'
        },
        {
            name: 'Evergreen',
            timeframe: 'Ongoing',
            icon: <BarChart3 className="h-5 w-5" />,
            color: 'text-teal-500',
            tasks: [
                { id: 'quarterly_promo', name: 'Quarterly price promotion campaigns', service: null },
                { id: 'continuous_ads', name: 'Maintain low-spend ad campaigns', service: null },
                { id: 'newsletter_swaps', name: 'Monthly newsletter swap participation', service: 'bookfunnel' },
                { id: 'series_funnel', name: 'Series read-through optimization', service: null },
                { id: 'backlist_promos', name: 'Backlist cross-promotion', service: null }
            ],
            totalReach: 'Recurring'
        }
    ];

    // ========================
    // ESKRYBE PACKAGES (with honest metrics)
    // ========================

    const eskrybePackages = [
        {
            id: 'starter',
            name: 'Launch Starter',
            price: 99,
            description: 'Essential launch foundation',
            services: 8,
            subscriberReach: '500K+',
            realisticImpressions: '50K-100K',
            expectedDownloads: '500-2,000',
            features: [
                '3 ARC platform submissions (BookSirens, BookSprout, Hidden Gems)',
                '3 promo site submissions (Robin Reads, Fussy Librarian, BKnights)',
                'LibraryThing Early Reviewers application',
                'Goodreads Giveaway setup',
                'Launch timing optimization',
                'Book description optimization',
                'Progress tracking dashboard'
            ],
            includes: {
                arc: ['booksirens', 'booksprout', 'hiddengems'],
                promo: ['robinreads', 'fussylibrarian', 'bknights'],
                free: ['librarything', 'goodreads']
            }
        },
        {
            id: 'professional',
            name: 'Launch Professional',
            price: 299,
            description: 'Comprehensive launch campaign',
            services: 15,
            subscriberReach: '2M+',
            realisticImpressions: '200K-500K',
            expectedDownloads: '2,000-10,000',
            popular: true,
            features: [
                'Everything in Starter, plus:',
                'NetGalley submission (via co-op)',
                '6 additional promo site submissions',
                'BookFunnel newsletter swap coordination (5 swaps)',
                'BookBub Featured Deal application & optimization',
                'Amazon keyword research & optimization',
                'Launch week stacking strategy',
                'Ad copy for BookBub & Amazon Ads',
                'Email announcement templates',
                'Weekly strategy calls during launch month'
            ],
            includes: {
                arc: ['netgalley', 'booksirens', 'booksprout', 'hiddengems', 'reedsy'],
                promo: ['freebooksy', 'ent', 'robinreads', 'fussylibrarian', 'bargainbooksy', 'bookgorilla', 'ohfb', 'kindlenationdaily', 'bknights'],
                free: ['librarything', 'goodreads'],
                swaps: ['bookfunnel']
            }
        },
        {
            id: 'bestseller',
            name: 'Bestseller Campaign',
            price: 799,
            description: 'Maximum reach for serious authors',
            services: 25,
            subscriberReach: '10M+',
            realisticImpressions: '1M-2M',
            expectedDownloads: '10,000-50,000',
            features: [
                'Everything in Professional, plus:',
                'Edelweiss+ submission for trade visibility',
                'Full NetGalley campaign management',
                'Reedsy Discovery professional reviews (3)',
                '10+ newsletter swap coordination via BookFunnel',
                'StoryOrigin promo management',
                'Coordinated multi-day launch stacking',
                'BookBub Ads campaign setup & management ($100 credit)',
                'Amazon Ads campaign setup & management ($100 credit)',
                'Blogger/podcaster outreach (20 contacts)',
                'Press release distribution',
                'Social media content calendar',
                'Post-launch momentum campaign',
                'Dedicated launch manager',
                'Money-back guarantee if under 1,000 downloads'
            ],
            includes: {
                arc: ['netgalley', 'edelweiss', 'booksirens', 'booksprout', 'hiddengems', 'reedsy', 'voraciousreaders'],
                promo: ['bookbub_featured', 'freebooksy', 'ent', 'robinreads', 'fussylibrarian', 'bargainbooksy', 'bookgorilla', 'ohfb', 'kindlenationdaily', 'readingdeals', 'bookbarbarian', 'bknights'],
                free: ['librarything', 'goodreads', 'storygraph'],
                swaps: ['bookfunnel', 'storyorigin'],
                ads: ['bookbub_ads', 'amazon_ads']
            }
        },
        {
            id: 'enterprise',
            name: 'Publishing House',
            price: 1999,
            description: 'Agency-level service for multiple titles',
            services: 'Unlimited',
            subscriberReach: '50M+',
            realisticImpressions: '5M-10M',
            expectedDownloads: '50,000-200,000',
            perYear: true,
            features: [
                'Everything in Bestseller for up to 4 titles/year',
                'Priority NetGalley placement',
                'Trade publication outreach (Publishers Weekly, Kirkus)',
                'Library marketing campaign (LibraryReads nomination support)',
                'Annual strategy planning',
                'Quarterly promotional calendars',
                'Backlist revitalization campaigns',
                'Series launch coordination',
                'Author brand development',
                'Speaking engagement support',
                'Film/TV rights pitch preparation',
                'Dedicated account manager',
                'Monthly ROI reporting'
            ]
        }
    ];

    // Calculate total potential reach
    const totalPotentialReach = () => {
        const arcReach = prelaunchServices.arc_distribution.reduce((sum, s) => {
            const num = parseInt(s.reach.replace(/[^0-9]/g, ''));
            return sum + (isNaN(num) ? 0 : num);
        }, 0);
        const launchReach = launchServices.reduce((sum, s) => {
            const num = parseInt(s.reach.replace(/[^0-9]/g, ''));
            return sum + (isNaN(num) ? 0 : num);
        }, 0);
        return arcReach + launchReach;
    };

    const toggleChecklist = (phaseIndex, taskId) => {
        const key = `${phaseIndex}-${taskId}`;
        setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const getPhaseProgress = (phaseIndex) => {
        const phase = launchPhases[phaseIndex];
        const completed = phase.tasks.filter(t => checklist[`${phaseIndex}-${t.id}`]).length;
        return Math.round((completed / phase.tasks.length) * 100);
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border bg-card">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={onBack} data-testid="back-btn">
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back
                        </Button>
                        <div>
                            <h1 className="font-serif text-2xl font-semibold flex items-center gap-2">
                                <Rocket className="h-6 w-6 text-accent" />
                                Ultimate Book Launch System
                            </h1>
                            <p className="text-sm text-muted-foreground">
                                Maximum reach: {(totalPotentialReach() / 1000000).toFixed(0)}M+ readers across {prelaunchServices.arc_distribution.length + launchServices.length}+ services
                            </p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                <Tabs defaultValue="phases">
                    <TabsList className="mb-6">
                        <TabsTrigger value="phases" data-testid="tab-phases">
                            <Calendar className="h-4 w-4 mr-2" />
                            Launch Phases
                        </TabsTrigger>
                        <TabsTrigger value="services" data-testid="tab-services">
                            <Globe className="h-4 w-4 mr-2" />
                            All Services
                        </TabsTrigger>
                        <TabsTrigger value="packages" data-testid="tab-packages">
                            <Zap className="h-4 w-4 mr-2" />
                            eSkrybe Packages
                        </TabsTrigger>
                        <TabsTrigger value="strategy" data-testid="tab-strategy">
                            <Target className="h-4 w-4 mr-2" />
                            Strategy Guide
                        </TabsTrigger>
                    </TabsList>

                    {/* PHASES TAB */}
                    <TabsContent value="phases">
                        {/* Phase Navigation */}
                        <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
                            {launchPhases.map((phase, index) => (
                                <Button
                                    key={index}
                                    variant={activePhase === index ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setActivePhase(index)}
                                    className="flex-shrink-0"
                                >
                                    <span className={phase.color}>{phase.icon}</span>
                                    <span className="ml-2">{phase.name}</span>
                                    <Badge variant="secondary" className="ml-2">{getPhaseProgress(index)}%</Badge>
                                </Button>
                            ))}
                        </div>

                        {/* Active Phase Detail */}
                        <Card className="mb-8">
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <CardTitle className="font-serif text-xl flex items-center gap-2">
                                            <span className={launchPhases[activePhase].color}>
                                                {launchPhases[activePhase].icon}
                                            </span>
                                            Phase {activePhase + 1}: {launchPhases[activePhase].name}
                                        </CardTitle>
                                        <CardDescription>
                                            {launchPhases[activePhase].timeframe} • Total Reach: {launchPhases[activePhase].totalReach}
                                        </CardDescription>
                                    </div>
                                    <Progress value={getPhaseProgress(activePhase)} className="w-32" />
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {launchPhases[activePhase].tasks.map((task) => (
                                        <div
                                            key={task.id}
                                            className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                                                checklist[`${activePhase}-${task.id}`] 
                                                    ? 'bg-success/10 border-success/30' 
                                                    : 'bg-card border-border hover:border-muted-foreground'
                                            }`}
                                        >
                                            <Checkbox
                                                checked={checklist[`${activePhase}-${task.id}`] || false}
                                                onCheckedChange={() => toggleChecklist(activePhase, task.id)}
                                            />
                                            <div className="flex-1">
                                                <div className="font-medium text-sm">{task.name}</div>
                                                {task.reach && (
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <Users className="h-3 w-3 text-muted-foreground" />
                                                        <span className="text-xs text-muted-foreground">{task.reach}</span>
                                                    </div>
                                                )}
                                            </div>
                                            {task.service && (
                                                <Badge variant="outline" className="text-xs">{task.service}</Badge>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Phase Navigation Arrows */}
                        <div className="flex justify-between">
                            <Button
                                variant="outline"
                                onClick={() => setActivePhase(Math.max(0, activePhase - 1))}
                                disabled={activePhase === 0}
                            >
                                <ChevronLeft className="h-4 w-4 mr-2" />
                                Previous Phase
                            </Button>
                            <Button
                                onClick={() => setActivePhase(Math.min(launchPhases.length - 1, activePhase + 1))}
                                disabled={activePhase === launchPhases.length - 1}
                            >
                                Next Phase
                                <ChevronRight className="h-4 w-4 ml-2" />
                            </Button>
                        </div>
                    </TabsContent>

                    {/* SERVICES TAB */}
                    <TabsContent value="services">
                        <div className="space-y-8">
                            {/* Pre-Launch ARC Services */}
                            <div>
                                <h2 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
                                    <Users className="h-5 w-5 text-purple-500" />
                                    Pre-Launch Reviewer Networks
                                    <Badge variant="secondary">{prelaunchServices.arc_distribution.length} services</Badge>
                                </h2>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {prelaunchServices.arc_distribution.map((service) => (
                                        <Card key={service.id} className="hover-lift">
                                            <CardContent className="pt-4">
                                                <div className="flex items-start justify-between mb-2">
                                                    <div>
                                                        <h3 className="font-semibold">{service.name}</h3>
                                                        <Badge variant={
                                                            service.type === 'premium' ? 'default' :
                                                            service.type === 'free' ? 'secondary' : 'outline'
                                                        } className="mt-1">{service.type}</Badge>
                                                    </div>
                                                    {service.autoSubmit && (
                                                        <Badge variant="outline" className="text-xs text-success border-success">
                                                            Auto-Submit
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="space-y-2 text-sm mt-3">
                                                    <div className="flex items-center gap-2">
                                                        <Users className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.reach}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.cost}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Star className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.reviewRate} review rate</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Clock className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.leadTime} lead time</span>
                                                    </div>
                                                </div>
                                                <a href={service.url} target="_blank" rel="noopener noreferrer" className="mt-4 block">
                                                    <Button variant="outline" size="sm" className="w-full">
                                                        Visit <ExternalLink className="h-3 w-3 ml-1" />
                                                    </Button>
                                                </a>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>

                            {/* Reader Magnet Platforms */}
                            <div>
                                <h2 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
                                    <Mail className="h-5 w-5 text-green-500" />
                                    List Building & Newsletter Swaps
                                    <Badge variant="secondary">{prelaunchServices.reader_magnets.length} services</Badge>
                                </h2>
                                <div className="grid md:grid-cols-3 gap-4">
                                    {prelaunchServices.reader_magnets.map((service) => (
                                        <Card key={service.id} className="hover-lift">
                                            <CardContent className="pt-4">
                                                <h3 className="font-semibold mb-2">{service.name}</h3>
                                                <div className="space-y-2 text-sm">
                                                    <div className="flex items-center gap-2">
                                                        <Users className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.reach}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.cost}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.listBuildRate}</span>
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-1 mt-3">
                                                    {service.features.map((f, i) => (
                                                        <Badge key={i} variant="secondary" className="text-xs">{f}</Badge>
                                                    ))}
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>

                            {/* Launch Promo Services */}
                            <div>
                                <h2 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2">
                                    <Megaphone className="h-5 w-5 text-red-500" />
                                    Launch Day Promotion Sites
                                    <Badge variant="secondary">{launchServices.length} services</Badge>
                                </h2>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {launchServices.map((service) => (
                                        <Card key={service.id} className="hover-lift">
                                            <CardContent className="pt-4">
                                                <div className="flex items-start justify-between mb-2">
                                                    <h3 className="font-semibold">{service.name}</h3>
                                                    <div className="flex">
                                                        {[...Array(5)].map((_, i) => (
                                                            <Star key={i} className={`h-3 w-3 ${i < service.effectiveness ? 'text-accent fill-accent' : 'text-muted'}`} />
                                                        ))}
                                                    </div>
                                                </div>
                                                <Badge variant={
                                                    service.category === 'premium' ? 'default' :
                                                    service.category === 'standard' ? 'secondary' : 'outline'
                                                }>{service.category}</Badge>
                                                <div className="space-y-2 text-sm mt-3">
                                                    <div className="flex items-center gap-2">
                                                        <Users className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.reach} readers</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.cost}</span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                                                        <span>{service.avgDownloads} avg downloads</span>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-muted-foreground mt-2">Best for: {service.bestFor}</p>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    {/* PACKAGES TAB */}
                    <TabsContent value="packages">
                        <div className="text-center mb-8">
                            <Badge variant="outline" className="mb-4">Coming Soon - Reserve Your Spot</Badge>
                            <h2 className="font-serif text-3xl font-semibold mb-2">eSkrybe Launch Packages</h2>
                            <p className="text-muted-foreground max-w-2xl mx-auto">
                                One-click automated submission to {prelaunchServices.arc_distribution.length + launchServices.length}+ services.
                                We handle timing, optimization, and follow-up.
                            </p>
                        </div>

                        {/* TRANSPARENCY TABLE - Being Honest About Numbers */}
                        <Card className="mb-8 border-warning/50 bg-warning/5">
                            <CardHeader>
                                <CardTitle className="font-serif flex items-center gap-2">
                                    <Shield className="h-5 w-5 text-warning" />
                                    Understanding Our Numbers (Transparency)
                                </CardTitle>
                                <CardDescription>
                                    We believe in honest marketing. Here's what the numbers actually mean:
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-border">
                                                <th className="text-left py-3 px-4 font-semibold">Metric</th>
                                                <th className="text-left py-3 px-4 font-semibold">What Services Claim</th>
                                                <th className="text-left py-3 px-4 font-semibold">Reality</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="border-b border-border/50">
                                                <td className="py-3 px-4 font-medium">Subscriber Count</td>
                                                <td className="py-3 px-4">"200,000 readers"</td>
                                                <td className="py-3 px-4 text-muted-foreground">Total email list size</td>
                                            </tr>
                                            <tr className="border-b border-border/50">
                                                <td className="py-3 px-4 font-medium">Email Open Rate</td>
                                                <td className="py-3 px-4">-</td>
                                                <td className="py-3 px-4 text-muted-foreground">~25-30% actually open emails</td>
                                            </tr>
                                            <tr className="border-b border-border/50">
                                                <td className="py-3 px-4 font-medium">Click-Through Rate</td>
                                                <td className="py-3 px-4">-</td>
                                                <td className="py-3 px-4 text-muted-foreground">~2-5% click on your book</td>
                                            </tr>
                                            <tr className="border-b border-border/50">
                                                <td className="py-3 px-4 font-medium">Actual Eyeballs</td>
                                                <td className="py-3 px-4">-</td>
                                                <td className="py-3 px-4 text-accent font-medium">~4,000-10,000 per service</td>
                                            </tr>
                                            <tr>
                                                <td className="py-3 px-4 font-medium">Goodreads "90M members"</td>
                                                <td className="py-3 px-4">"Reach 90 million"</td>
                                                <td className="py-3 px-4 text-muted-foreground">Giveaway gets 500-5,000 entries</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-xs text-muted-foreground mt-4 italic">
                                    We show you both "subscriber reach" (what services claim) and "expected downloads" (realistic results) so you can make informed decisions.
                                </p>
                            </CardContent>
                        </Card>

                        {/* Package Comparison Table */}
                        <Card className="mb-8">
                            <CardHeader>
                                <CardTitle className="font-serif">Package Comparison (Honest Numbers)</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-border">
                                                <th className="text-left py-3 px-4 font-semibold">Package</th>
                                                <th className="text-left py-3 px-4 font-semibold">Price</th>
                                                <th className="text-left py-3 px-4 font-semibold">Services</th>
                                                <th className="text-left py-3 px-4 font-semibold text-muted-foreground">Subscriber Reach*</th>
                                                <th className="text-left py-3 px-4 font-semibold">Realistic Impressions</th>
                                                <th className="text-left py-3 px-4 font-semibold text-accent">Expected Downloads</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {eskrybePackages.map((pkg) => (
                                                <tr key={pkg.id} className="border-b border-border/50">
                                                    <td className="py-3 px-4 font-medium">{pkg.name}</td>
                                                    <td className="py-3 px-4">${pkg.price}{pkg.perYear ? '/yr' : ''}</td>
                                                    <td className="py-3 px-4">{pkg.services}</td>
                                                    <td className="py-3 px-4 text-muted-foreground">{pkg.subscriberReach}</td>
                                                    <td className="py-3 px-4">{pkg.realisticImpressions}</td>
                                                    <td className="py-3 px-4 font-semibold text-accent">{pkg.expectedDownloads}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-xs text-muted-foreground mt-4">
                                    * "Subscriber Reach" is total subscribers across all included services. Actual results depend on your book's cover, description, reviews, genre, and timing.
                                </p>
                            </CardContent>
                        </Card>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {eskrybePackages.map((pkg) => (
                                <Card 
                                    key={pkg.id}
                                    className={`relative ${pkg.popular ? 'ring-2 ring-accent' : ''} ${selectedPackage === pkg.id ? 'border-accent' : ''}`}
                                >
                                    {pkg.popular && (
                                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                            <Badge className="bg-accent">Most Popular</Badge>
                                        </div>
                                    )}
                                    <CardHeader>
                                        <CardTitle className="font-serif">{pkg.name}</CardTitle>
                                        <CardDescription>{pkg.description}</CardDescription>
                                        <div className="flex items-baseline gap-1 mt-2">
                                            <span className="font-serif text-4xl font-bold">${pkg.price}</span>
                                            <span className="text-muted-foreground">{pkg.perYear ? '/year' : '/launch'}</span>
                                        </div>
                                        <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                                            <div className="text-xs text-muted-foreground mb-1">Expected Results</div>
                                            <div className="font-serif text-lg font-bold text-accent">{pkg.expectedDownloads}</div>
                                            <div className="text-xs text-muted-foreground">downloads</div>
                                        </div>
                                        <div className="flex items-center gap-4 mt-2 text-sm">
                                            <div className="flex items-center gap-1">
                                                <Globe className="h-4 w-4 text-muted-foreground" />
                                                <span>{pkg.services} services</span>
                                            </div>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <ul className="space-y-2">
                                            {pkg.features.slice(0, 6).map((feature, i) => (
                                                <li key={i} className="flex items-start gap-2 text-sm">
                                                    <Check className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                            {pkg.features.length > 6 && (
                                                <li className="text-sm text-muted-foreground">
                                                    + {pkg.features.length - 6} more features...
                                                </li>
                                            )}
                                        </ul>
                                        <Button 
                                            className="w-full mt-6" 
                                            variant={selectedPackage === pkg.id ? "default" : "outline"}
                                            onClick={() => setSelectedPackage(pkg.id)}
                                        >
                                            {selectedPackage === pkg.id ? 'Selected' : 'Select Package'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>

                        {/* ROI Calculator - Honest Numbers */}
                        <Card className="mt-8">
                            <CardHeader>
                                <CardTitle className="font-serif">Realistic ROI Expectations</CardTitle>
                                <CardDescription>Based on typical results from coordinated book launch campaigns</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="grid md:grid-cols-4 gap-4 mb-6">
                                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                                        <div className="font-serif text-2xl font-bold text-muted-foreground">104M+</div>
                                        <div className="text-sm text-muted-foreground">Total Subscribers*</div>
                                        <div className="text-xs mt-2 italic">*Combined list size (not actual reach)</div>
                                    </div>
                                    <div className="text-center p-4 bg-accent/10 rounded-lg border border-accent/30">
                                        <div className="font-serif text-2xl font-bold text-accent">500K-2M</div>
                                        <div className="text-sm font-medium">Realistic Impressions</div>
                                        <div className="text-xs mt-2">People who will actually see your book</div>
                                    </div>
                                    <div className="text-center p-4 bg-success/10 rounded-lg border border-success/30">
                                        <div className="font-serif text-2xl font-bold text-success">2K-50K</div>
                                        <div className="text-sm font-medium">Expected Downloads</div>
                                        <div className="text-xs mt-2">Depends on cover, description, reviews</div>
                                    </div>
                                    <div className="text-center p-4 bg-muted/50 rounded-lg">
                                        <div className="font-serif text-2xl font-bold">$2,000+</div>
                                        <div className="text-sm text-muted-foreground">DIY Cost Savings</div>
                                        <div className="text-xs mt-2">20+ hours research + individual fees</div>
                                    </div>
                                </div>
                                
                                {/* ROI Breakdown Table */}
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-border">
                                                <th className="text-left py-2 px-3 font-semibold">Package</th>
                                                <th className="text-left py-2 px-3 font-semibold">Investment</th>
                                                <th className="text-left py-2 px-3 font-semibold">Expected Downloads</th>
                                                <th className="text-left py-2 px-3 font-semibold">Cost Per Download</th>
                                                <th className="text-left py-2 px-3 font-semibold">Potential Revenue*</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr className="border-b border-border/50">
                                                <td className="py-2 px-3">Launch Starter</td>
                                                <td className="py-2 px-3">$99</td>
                                                <td className="py-2 px-3">500-2,000</td>
                                                <td className="py-2 px-3 text-muted-foreground">$0.05-$0.20</td>
                                                <td className="py-2 px-3">$250-$1,000</td>
                                            </tr>
                                            <tr className="border-b border-border/50 bg-accent/5">
                                                <td className="py-2 px-3 font-medium">Launch Professional</td>
                                                <td className="py-2 px-3">$299</td>
                                                <td className="py-2 px-3">2,000-10,000</td>
                                                <td className="py-2 px-3 text-muted-foreground">$0.03-$0.15</td>
                                                <td className="py-2 px-3 text-accent font-medium">$1,000-$5,000</td>
                                            </tr>
                                            <tr className="border-b border-border/50">
                                                <td className="py-2 px-3">Bestseller Campaign</td>
                                                <td className="py-2 px-3">$799</td>
                                                <td className="py-2 px-3">10,000-50,000</td>
                                                <td className="py-2 px-3 text-muted-foreground">$0.02-$0.08</td>
                                                <td className="py-2 px-3">$5,000-$25,000</td>
                                            </tr>
                                            <tr>
                                                <td className="py-2 px-3">Publishing House</td>
                                                <td className="py-2 px-3">$1,999/yr</td>
                                                <td className="py-2 px-3">50,000-200,000</td>
                                                <td className="py-2 px-3 text-muted-foreground">$0.01-$0.04</td>
                                                <td className="py-2 px-3">$25,000-$100,000</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-xs text-muted-foreground mt-4">
                                    * Potential revenue assumes 5-10% of downloads convert to paid sales at $4.99 average book price. 
                                    Actual results vary widely based on book quality, cover design, description, reviews, genre, and market timing.
                                    These are estimates based on industry data and should not be considered guarantees.
                                </p>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* STRATEGY TAB */}
                    <TabsContent value="strategy">
                        <div className="max-w-4xl mx-auto space-y-8">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-serif text-2xl">The Bestseller Blueprint</CardTitle>
                                    <CardDescription>How top indie authors consistently hit bestseller lists</CardDescription>
                                </CardHeader>
                                <CardContent className="prose prose-sm max-w-none">
                                    <h3 className="font-serif text-lg">The Secret: Coordinated Velocity</h3>
                                    <p>
                                        Amazon's algorithm rewards <strong>sales velocity</strong> - the speed at which sales occur. 
                                        A coordinated launch creates a spike that triggers algorithmic promotion, creating a virtuous cycle.
                                    </p>

                                    <div className="not-prose my-6">
                                        <div className="bg-muted/50 p-6 rounded-lg">
                                            <h4 className="font-semibold mb-4">The Stacking Strategy</h4>
                                            <div className="grid md:grid-cols-3 gap-4">
                                                <div className="p-4 bg-background rounded-lg border">
                                                    <div className="font-semibold text-accent mb-2">Day 1 (Launch)</div>
                                                    <ul className="text-sm space-y-1">
                                                        <li>• BookBub Featured (if accepted)</li>
                                                        <li>• Freebooksy</li>
                                                        <li>• Full email list blast</li>
                                                        <li>• All social channels</li>
                                                    </ul>
                                                </div>
                                                <div className="p-4 bg-background rounded-lg border">
                                                    <div className="font-semibold text-accent mb-2">Day 2-3</div>
                                                    <ul className="text-sm space-y-1">
                                                        <li>• ENT</li>
                                                        <li>• Fussy Librarian</li>
                                                        <li>• Robin Reads</li>
                                                        <li>• Amazon Ads launch</li>
                                                    </ul>
                                                </div>
                                                <div className="p-4 bg-background rounded-lg border">
                                                    <div className="font-semibold text-accent mb-2">Day 4-7</div>
                                                    <ul className="text-sm space-y-1">
                                                        <li>• Bargain Booksy</li>
                                                        <li>• BookGorilla</li>
                                                        <li>• Secondary sites</li>
                                                        <li>• BookBub Ads</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <h3 className="font-serif text-lg">Category Strategy</h3>
                                    <p>
                                        Choose 2-3 categories where you can realistically hit #1. Being #1 in a small category 
                                        is better than #500 in a large one - it triggers "Bestseller" badges and recommendations.
                                    </p>

                                    <h3 className="font-serif text-lg">The Review Threshold</h3>
                                    <p>
                                        <strong>50+ reviews</strong> is the magic number where Amazon's algorithm starts actively 
                                        recommending your book. Use ARC campaigns to hit this before or during launch week.
                                    </p>

                                    <h3 className="font-serif text-lg">Price Strategy</h3>
                                    <div className="not-prose">
                                        <div className="grid md:grid-cols-3 gap-4 my-4">
                                            <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                                                <div className="font-semibold mb-1">Free ($0)</div>
                                                <div className="text-sm text-muted-foreground">Maximum downloads, list building, series starter</div>
                                            </div>
                                            <div className="p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
                                                <div className="font-semibold mb-1">$0.99</div>
                                                <div className="text-sm text-muted-foreground">Impulse buys, still counts as "sale" for rank</div>
                                            </div>
                                            <div className="p-4 bg-purple-500/10 rounded-lg border border-purple-500/30">
                                                <div className="font-semibold mb-1">$2.99-$4.99</div>
                                                <div className="text-sm text-muted-foreground">70% royalty, perceived value, serious readers</div>
                                            </div>
                                        </div>
                                    </div>

                                    <h3 className="font-serif text-lg">The Long Game</h3>
                                    <p>
                                        Bestseller status is temporary - sustainable income comes from:
                                    </p>
                                    <ul>
                                        <li><strong>Series read-through:</strong> Book 1 free/cheap drives sales of sequels</li>
                                        <li><strong>Email list:</strong> Direct relationship with readers</li>
                                        <li><strong>Backlist:</strong> Each new book promotes all previous books</li>
                                        <li><strong>Quarterly promotions:</strong> 4 big pushes/year keeps momentum</li>
                                    </ul>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle className="font-serif">Budget Allocation Guide</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div className="p-4 bg-muted/50 rounded-lg">
                                            <div className="font-semibold mb-2">Minimal Budget ($100-200)</div>
                                            <div className="text-sm text-muted-foreground mb-2">For testing or tight budgets</div>
                                            <div className="flex flex-wrap gap-2">
                                                <Badge>LibraryThing (FREE)</Badge>
                                                <Badge>BookSprout Free Tier</Badge>
                                                <Badge>Robin Reads ($40)</Badge>
                                                <Badge>Fussy Librarian ($25)</Badge>
                                                <Badge>BKnights ($20)</Badge>
                                                <Badge>BookFunnel Swaps</Badge>
                                            </div>
                                        </div>
                                        <div className="p-4 bg-muted/50 rounded-lg">
                                            <div className="font-semibold mb-2">Standard Budget ($300-500)</div>
                                            <div className="text-sm text-muted-foreground mb-2">Solid launch campaign</div>
                                            <div className="flex flex-wrap gap-2">
                                                <Badge>Everything above, plus:</Badge>
                                                <Badge>Freebooksy ($100)</Badge>
                                                <Badge>ENT ($75)</Badge>
                                                <Badge>BookSirens ($50)</Badge>
                                                <Badge>Hidden Gems ($30)</Badge>
                                                <Badge>Goodreads Giveaway ($119)</Badge>
                                            </div>
                                        </div>
                                        <div className="p-4 bg-accent/10 rounded-lg border border-accent/30">
                                            <div className="font-semibold mb-2">Premium Budget ($800-1500)</div>
                                            <div className="text-sm text-muted-foreground mb-2">Maximum impact launch</div>
                                            <div className="flex flex-wrap gap-2">
                                                <Badge>Everything above, plus:</Badge>
                                                <Badge>NetGalley ($100 co-op)</Badge>
                                                <Badge>Reedsy Reviews ($150)</Badge>
                                                <Badge>BookBub Ads ($200)</Badge>
                                                <Badge>Amazon Ads ($200)</Badge>
                                                <Badge>All standard promo sites</Badge>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
};

export default UltimateBookLaunch;
